import {
  byCostCenterCode,
  COST_CENTER_LEVELS,
  costCenterHierarchyOf,
  costCenterLabel,
  costCenterParentFor,
  type CostCenterLevel,
} from '@loot/shared';
import { useTranslation } from 'react-i18next';
import { api } from '../../api.ts';
import { actPayload, emptyAct, type ActForm } from './appointment-fields.tsx';
import { Field, Input, issueFor, Select } from '../../components/ui.tsx';

/**
 * Centro de custo: tipos, conversões e campos do formulário. Ficam fora da tela Centros de Custos
 * (que carrega sob demanda) porque a ficha da Empresa também os usa.
 */

export interface CostCenter {
  id: string;
  legalEntityId: string;
  legalEntityCode: string | null;
  departmentId: string | null;
  parentId: string | null;
  parentCode: string | null;
  code: string;
  name: string;
  managerUserId: string | null;
  managerName: string | null;
  substituteUserId: string | null;
  substituteName: string | null;
  isActive: boolean;
  validFrom: string;
  validUntil: string | null;
  netsuiteCode: string | null;
  canEdit: boolean;
  /** Nível (ADR 0047): organização e consulta, sem alçada. */
  structureLevel: CostCenterLevel | null;
  /** Master Responsável calculado (ADR 0099): a referência do Master ou o gestor dele. */
  masterResponsibleId: string | null;
  masterResponsibleName: string | null;
  /** Só no Master: referência organizacional diferente do gestor. */
  masterReferencePersonId: string | null;
  masterCostCenterId: string | null;
  managerialCostCenterId: string | null;
}

export interface CostCenterOptions {
  entities: { id: string; code: string; name: string }[];
  /** Departamentos da empresa e corporativos (sem empresa, valem para todas). */
  departments: { id: string; code: string; name: string; legalEntityId: string | null }[];
  people: { id: string; displayName: string }[];
}

export type CostCenterForm = {
  legalEntityId: string;
  departmentId: string;
  parentId: string;
  code: string;
  name: string;
  managerUserId: string;
  substituteUserId: string;
  isActive: boolean;
  validFrom: string;
  validUntil: string;
  netsuiteCode: string;
  structureLevel: CostCenterLevel | '';
  /** Retirar a referência organizacional do Master (ADR 0099). */
  removeMasterReference: boolean;
  /** Ato de nomeação do gestor e do substituto, quando mudam (entrega 5). */
  managerAct: ActForm;
  substituteAct: ActForm;
};
type Form = CostCenterForm;

const today = () => new Date().toISOString().slice(0, 10);

export function costCenterToForm(c?: CostCenter): Form {
  return {
    legalEntityId: c?.legalEntityId ?? '',
    departmentId: c?.departmentId ?? '',
    parentId: c?.parentId ?? '',
    code: c?.code ?? '',
    name: c?.name ?? '',
    managerUserId: c?.managerUserId ?? '',
    substituteUserId: c?.substituteUserId ?? '',
    isActive: c?.isActive ?? true,
    validFrom: c?.validFrom ?? today(),
    validUntil: c?.validUntil ?? '',
    netsuiteCode: c?.netsuiteCode ?? '',
    structureLevel: c?.structureLevel ?? '',
    removeMasterReference: false,
    managerAct: emptyAct(),
    substituteAct: emptyAct(),
  };
}

/**
 * Centro superior como "código - nome", procurado na própria lista pelo `parentId`; se não estiver
 * nela (sem acesso, por exemplo), fica só o código que o servidor manda.
 */
export function parentLabel(c: CostCenter, list: CostCenter[]): string | null {
  if (!c.parentId) return c.parentCode;
  const p = list.find((x) => x.id === c.parentId);
  return p ? costCenterLabel(p.code, p.name) : c.parentCode;
}

/** Grava o centro de custo (o mesmo corpo na tela Centros de Custos e na ficha da Empresa). */
export function saveCostCenter({ id, form }: { id: string | null; form: Form }) {
  const { managerAct, substituteAct, removeMasterReference, ...rest } = form;
  const body = {
    ...rest,
    managerAppointment: actPayload(managerAct),
    substituteAppointment: actPayload(substituteAct),
    departmentId: form.departmentId || null,
    parentId: form.parentId || null,
    managerUserId: form.managerUserId || null,
    substituteUserId: form.substituteUserId || null,
    validUntil: form.validUntil || null,
    netsuiteCode: form.netsuiteCode || null,
    structureLevel: form.structureLevel || null,
    ...(removeMasterReference ? { masterReferencePersonId: null } : {}),
  };
  return id ? api.put(`/core/cost-centers/${id}`, body) : api.post('/core/cost-centers', body);
}

/**
 * Campos do centro de custo, usados na tela Centros de Custos e na ficha da Empresa. Na ficha a
 * empresa vem fixa (`lockedEntity`).
 */
export function CostCenterFields({
  id,
  form: f,
  set,
  options,
  list,
  error,
  lockedEntity = false,
}: {
  id: string | null;
  form: Form;
  set: (patch: Partial<Form>) => void;
  options: CostCenterOptions;
  list: CostCenter[];
  error: unknown;
  lockedEntity?: boolean;
}) {
  const { t } = useTranslation();
  // Master e Gerencial (adendo 2 do ADR 0047): calculados pelo superior direto, o único gravado.
  const byId = new Map(list.map((c) => [c.id, c]));
  const { masterId: selMaster, managerialId: selManagerial } = costCenterHierarchyOf(
    f.parentId || null,
    byId,
  );
  // Opções: mesma empresa, nível certo e ativas; o valor já gravado continua na lista (marcado
  // como inativo) para nunca sumir sem a pessoa ver.
  const choicesOf = (level: CostCenterLevel, keep: string | null, under?: string | null) =>
    list
      .filter(
        (c) =>
          c.legalEntityId === f.legalEntityId &&
          c.id !== id &&
          c.structureLevel === level &&
          (c.isActive || c.id === keep) &&
          (under === undefined || c.parentId === under),
      )
      .sort(byCostCenterCode);
  const masters = choicesOf('master', selMaster);
  const managerials = selMaster ? choicesOf('managerial', selManagerial, selMaster) : [];
  const centerOption = (c: CostCenter) => (
    <option key={c.id} value={c.id}>
      {costCenterLabel(c.code, c.name)}
      {c.isActive ? '' : ` (${t('governance.costCenters.hierarchy.inactive')})`}
    </option>
  );
  const labelOf = (cid: string | null) => {
    const c = cid ? byId.get(cid) : undefined;
    return c ? costCenterLabel(c.code, c.name) : '—';
  };

  // Centro novo e ativo: Nível e gestor obrigatórios (ADR 0047, adendo). Os centros que já existem
  // continuam editáveis sem eles enquanto são regularizados.
  const requiredNew = !id && f.isActive;
  // Master Responsável calculado (ADR 0099): no Master, o próprio gestor do formulário; abaixo dele,
  // o gestor do Master. A referência organizacional, quando há, vale no lugar do gestor.
  const ownMaster = f.structureLevel === 'master';
  const masterRow = ownMaster && id ? byId.get(id) : selMaster ? byId.get(selMaster) : undefined;
  const hasReference = !!masterRow?.masterReferencePersonId && !f.removeMasterReference;
  const managerNameOf = (uid: string | null | undefined) =>
    options.people.find((u) => u.id === uid)?.displayName ?? null;
  const masterName = hasReference
    ? masterRow?.masterResponsibleName
    : ownMaster
      ? managerNameOf(f.managerUserId)
      : (masterRow?.masterResponsibleName ?? managerNameOf(masterRow?.managerUserId));
  // Trocar o Master limpa o Gerencial que não pertence a ele; trocar o Gerencial mantém o Master.
  const chooseMaster = (masterId: string) =>
    set({
      parentId:
        costCenterParentFor(
          f.structureLevel || 'operational',
          masterId || null,
          selManagerial && byId.get(selManagerial)?.parentId === masterId ? selManagerial : null,
        ) ?? '',
    });
  const chooseManagerial = (managerialId: string) =>
    set({ parentId: costCenterParentFor('operational', selMaster, managerialId || null) ?? '' });
  const levelLabel = (level: string | null) =>
    level ? t(`governance.costCenters.levels.${level}`) : t('governance.costCenters.noLevel');
  const ownLabel = costCenterLabel(f.code, f.name) || '—';
  const box =
    f.structureLevel === 'master'
      ? { master: ownLabel, managerial: '—' }
      : f.structureLevel === 'managerial'
        ? { master: labelOf(selMaster), managerial: ownLabel }
        : f.structureLevel === 'operational'
          ? { master: labelOf(selMaster), managerial: labelOf(selManagerial) }
          : { master: '—', managerial: '—' };
  return (
    <>
      {/* Quadro "Master associado": só leitura, calculado pela cadeia de superiores. */}
      <div className="rounded-lg border border-rule bg-paper p-3 text-sm sm:col-span-2">
        <div className="mb-1 font-medium">{t('governance.costCenters.hierarchy.box')}</div>
        <dl className="grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-2">
          <div>
            <dt className="text-xs text-muted">{t('governance.fields.legalEntity')}</dt>
            <dd>{options.entities.find((e) => e.id === f.legalEntityId)?.code ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted">{t('governance.costCenters.level')}</dt>
            <dd>{levelLabel(f.structureLevel || null)}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted">{t('governance.costCenters.hierarchy.master')}</dt>
            <dd>{box.master}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted">
              {t('governance.costCenters.hierarchy.managerial')}
            </dt>
            <dd>{box.managerial}</dd>
          </div>
        </dl>
      </div>
      <Field label={t('governance.fields.legalEntity')} required>
        <Select
          required
          disabled={!!id || lockedEntity}
          value={f.legalEntityId}
          onChange={(ev) => set({ legalEntityId: ev.target.value, departmentId: '', parentId: '' })}
        >
          <option value="">{t('common.choose')}</option>
          {options.entities.map((e) => (
            <option key={e.id} value={e.id}>
              {e.code} · {e.name}
            </option>
          ))}
        </Select>
      </Field>
      <Field label={t('governance.fields.code')} error={issueFor(error, 'code')} required>
        <Input
          required
          maxLength={40}
          value={f.code}
          onChange={(ev) => set({ code: ev.target.value.toUpperCase() })}
        />
      </Field>
      <Field label={t('governance.fields.name')} error={issueFor(error, 'name')} required>
        <Input
          required
          maxLength={200}
          value={f.name}
          onChange={(ev) => set({ name: ev.target.value })}
        />
      </Field>
      <Field
        label={t('governance.fields.department')}
        hint={t('governance.optional')}
        error={issueFor(error, 'departmentId')}
      >
        <Select value={f.departmentId} onChange={(ev) => set({ departmentId: ev.target.value })}>
          <option value="">—</option>
          {/* Cadastro oficial de Departamentos: os da empresa do centro e os corporativos. */}
          {options.departments
            .filter((d) => d.legalEntityId === f.legalEntityId || d.legalEntityId === null)
            .map((d) => (
              <option key={d.id} value={d.id}>
                {d.code} · {d.name}
                {d.legalEntityId === null
                  ? ` (${t('governance.costCenters.corporateDepartment')})`
                  : ''}
              </option>
            ))}
        </Select>
      </Field>
      <Field
        label={t('governance.costCenters.level')}
        hint={t('governance.costCenters.levelHint')}
        error={issueFor(error, 'structureLevel')}
        required={requiredNew}
      >
        <Select
          required={requiredNew}
          value={f.structureLevel}
          onChange={(ev) => {
            const level = ev.target.value as Form['structureLevel'];
            // Sem nível: o superior atual fica. Com nível, vale o que continua válido: o Master
            // não tem superior; o Gerencial fica no Master; o Operacional mantém Master e Gerencial.
            const parentId = level
              ? (costCenterParentFor(
                  level,
                  selMaster,
                  level === 'operational' ? selManagerial : null,
                ) ?? '')
              : f.parentId;
            set({ structureLevel: level, parentId });
          }}
        >
          <option value="">{t('governance.costCenters.noLevel')}</option>
          {COST_CENTER_LEVELS.map((l) => (
            <option key={l} value={l}>
              {t(`governance.costCenters.levels.${l}`)}
            </option>
          ))}
        </Select>
      </Field>
      {!f.structureLevel && (
        // Centro sem nível (em regularização): o superior atual fica como está.
        <Field
          label={t('governance.costCenters.hierarchy.currentParent')}
          hint={t('governance.costCenters.hierarchy.currentParentHint')}
        >
          <Input readOnly value={f.parentId ? labelOf(f.parentId) : '—'} />
        </Field>
      )}
      {(f.structureLevel === 'managerial' || f.structureLevel === 'operational') && (
        <Field
          label={t('governance.costCenters.hierarchy.master')}
          hint={t('governance.costCenters.hierarchy.masterHint')}
          error={issueFor(error, 'parentId')}
          required
        >
          <Select required value={selMaster ?? ''} onChange={(ev) => chooseMaster(ev.target.value)}>
            <option value="">{t('common.choose')}</option>
            {masters.map(centerOption)}
          </Select>
        </Field>
      )}
      {f.structureLevel === 'operational' && (
        <Field
          label={t('governance.costCenters.hierarchy.managerial')}
          hint={
            selMaster && !selManagerial && managerials.length > 0
              ? t('governance.costCenters.hierarchy.managerialAvailable')
              : t('governance.costCenters.hierarchy.managerialHint')
          }
        >
          <Select
            value={selManagerial ?? ''}
            disabled={!selMaster}
            onChange={(ev) => chooseManagerial(ev.target.value)}
          >
            <option value="">{t('governance.costCenters.hierarchy.directToMaster')}</option>
            {managerials.map(centerOption)}
          </Select>
        </Field>
      )}
      <Field label={t('governance.fields.netsuiteCode')} hint={t('governance.netsuiteHint')}>
        <Input
          maxLength={100}
          value={f.netsuiteCode}
          onChange={(ev) => set({ netsuiteCode: ev.target.value })}
        />
      </Field>
      <Field label={t('governance.fields.validFrom')} error={issueFor(error, 'validFrom')} required>
        <Input
          type="date"
          required
          value={f.validFrom}
          onChange={(ev) => set({ validFrom: ev.target.value })}
        />
      </Field>
      <Field
        label={t('governance.fields.validUntil')}
        hint={t('governance.optional')}
        error={issueFor(error, 'validUntil')}
      >
        <Input
          type="date"
          value={f.validUntil}
          onChange={(ev) => set({ validUntil: ev.target.value })}
        />
      </Field>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={f.isActive}
          onChange={(ev) => set({ isActive: ev.target.checked })}
        />
        {t('governance.fields.active')}
      </label>
      <div className="flex flex-col gap-1 sm:col-span-2">
        <span className="font-medium">{t('governance.costCenters.approval.title')}</span>
        <span className="text-xs text-muted">{t('governance.costCenters.approval.help')}</span>
      </div>
      <Field
        label={t('governance.costCenters.approval.approver')}
        hint={t('governance.costCenters.managerHint')}
        error={issueFor(error, 'managerUserId')}
        required={requiredNew}
      >
        <Select
          required={requiredNew}
          value={f.managerUserId}
          onChange={(ev) => set({ managerUserId: ev.target.value })}
        >
          <option value="">—</option>
          {options.people.map((u) => (
            <option key={u.id} value={u.id}>
              {u.displayName}
            </option>
          ))}
        </Select>
      </Field>
      <Field
        label={t('governance.costCenters.approval.substitute')}
        hint={t('governance.optional')}
      >
        <Select
          value={f.substituteUserId}
          onChange={(ev) => set({ substituteUserId: ev.target.value })}
        >
          <option value="">—</option>
          {options.people.map((u) => (
            <option key={u.id} value={u.id}>
              {u.displayName}
            </option>
          ))}
        </Select>
      </Field>
      {f.structureLevel && (
        <Field
          label={t('governance.costCenters.responsibles.master')}
          hint={
            hasReference
              ? t('governance.costCenters.responsibles.reference')
              : f.removeMasterReference
                ? t('governance.costCenters.responsibles.referenceRemoved')
                : t('governance.costCenters.responsibles.calculated')
          }
        >
          <Input readOnly value={masterName ?? '—'} />
          {hasReference && ownMaster && (
            <button
              type="button"
              className="text-left text-xs text-accent underline"
              onClick={() => set({ removeMasterReference: true })}
            >
              {t('governance.costCenters.responsibles.removeReference')}
            </button>
          )}
        </Field>
      )}
    </>
  );
}
